<template>
  <div class="mx-sm-1 mt-9">
    <div class="page-header">
      <h1 class="page-title fw-bold">Familles</h1>
    </div>

    <div class="card">
      <div class="card-header d-flex justify-content-between align-items-center">
        <h3 class="card-title fw-bold mb-0">Liste des Familles</h3>
        <div class="card-options d-flex gap-2">
          <button class="btn btn-outline-success btn-sm" @click="showCreateFamilyModal">
            <i class="fe fe-plus-circle me-1"></i> Ajouter
          </button>
          <button class="btn btn-outline-info btn-sm" @click="exportCSV">
            <i class="fe fe-download me-1"></i> Exporter
          </button>
          <button class="btn btn-outline-primary btn-sm" @click="triggerFileInput">
            <i class="fe fe-upload me-1"></i> Importer
          </button>
          <input type="file" ref="fileInput" class="d-none" accept=".xlsx, .xls" @change="importCSV" />
        </div>
      </div>

      <div class="card-body">
        <TableWithFilters
            :columns="columns"
            :filterableFields="filterableFields"
            :row-actions="rowActions"
            :items="families"
            @edit="onEdit"
            @delete="onDelete"
        >
          <template #cell-code="{ item }">
            <strong>{{ item.code }}</strong>
          </template>
          <template #cell-label="{ item }">
            {{ item.label }}
          </template>
          <template #cell-description="{ item }">
            {{ item.description }}
          </template>
        </TableWithFilters>
      </div>
    </div>

    <!-- Modal de création -->
    <modal-create-family
        :family="selectedFamily"
        @refresh="loadFamilies"
    />

    <div class="modal fade"
         id="modal-import-preview"
         tabindex="-1"
         role="dialog"
         data-bs-backdrop="static"
         data-bs-keyboard="false">
      <div class="modal-dialog modal-xl" role="document">
        <div class="modal-content">
          <div class="modal-header bg-secondary text-white">
            <h5 class="modal-title">Prévisualisation de l'import</h5>
            <button type="button" class="btn-close" data-bs-dismiss="modal" aria-label="Fermer"
                    @click="resetCsvPreview"></button>
          </div>
          <div class="modal-body">
            <div class="mb-3 d-flex flex-wrap gap-2">
              <span class="badge bg-secondary">Total : {{ csvPreview.total }}</span>
              <span class="badge bg-success">À créer : {{ csvPreview.toCreate.length }}</span>
              <span class="badge bg-info">Existants (ignorés) : {{ csvPreview.existingDb.length }}</span>
              <span class="badge bg-warning text-dark">Doublons fichier : {{ csvPreview.duplicatesFile.length }}</span>
              <span class="badge bg-danger">Invalides : {{ csvPreview.invalid.length }}</span>
            </div>

            <div v-if="csvPreview.toCreate.length">
              <h6 class="fw-bold mb-2">Lignes à créer</h6>
              <div class="table-responsive mb-4">
                <table class="table table-sm table-bordered text-nowrap">
                  <thead class="bg-light">
                  <tr>
                    <th>code</th>
                    <th>label</th>
                    <th>Description</th>
                  </tr>
                  </thead>
                  <tbody>
                  <tr v-for="(r, i) in csvPreview.toCreate" :key="'create-'+i">
                    <td>{{ r.code }}</td>
                    <td>{{ r.label }}</td>
                    <td>{{ r.description }}</td>
                  </tr>
                  </tbody>
                </table>
              </div>
            </div>

            <details v-if="csvPreview.existingDb.length" class="mb-3">
              <summary class="fw-bold">Déjà existants (ignorés)</summary>
              <div class="table-responsive mt-2">
                <table class="table table-sm table-bordered text-nowrap">
                  <thead class="bg-light"><tr><th>code</th><th>label</th></tr></thead>
                  <tbody>
                  <tr v-for="(r, i) in csvPreview.existingDb" :key="'exist-'+i">
                    <td>{{ r.code }}</td>
                    <td>{{ r.label }}</td>
                    <td>{{ r.description }}</td>
                  </tr>
                  </tbody>
                </table>
              </div>
            </details>

            <details v-if="csvPreview.duplicatesFile.length" class="mb-3">
              <summary class="fw-bold">Doublons dans le fichier</summary>
              <div class="table-responsive mt-2">
                <table class="table table-sm table-bordered text-nowrap">
                  <thead class="bg-light"><tr><th>code</th><th>label</th></tr></thead>
                  <tbody>
                  <tr v-for="(r, i) in csvPreview.duplicatesFile" :key="'dup-'+i">
                    <td>{{ r.code }}</td>
                    <td>{{ r.label }}</td>
                    <td>{{ r.description }}</td>
                  </tr>
                  </tbody>
                </table>
              </div>
            </details>

            <details v-if="csvPreview.invalid.length" class="mb-3">
              <summary class="fw-bold">Invalides (champ manquant)</summary>
              <div class="table-responsive mt-2">
                <table class="table table-sm table-bordered text-nowrap">
                  <thead class="bg-light"><tr><th>code</th><th>label</th></tr></thead>
                  <tbody>
                  <tr v-for="(r, i) in csvPreview.invalid" :key="'inv-'+i">
                    <td>{{ r.code }}</td>
                    <td>{{ r.label }}</td>
                    <td>{{ r.description }}</td>
                  </tr>
                  </tbody>
                </table>
              </div>
            </details>
          </div>

          <div class="modal-footer">
            <button type="button" class="btn btn-gray" data-bs-dismiss="modal" @click="resetCsvPreview">
              Fermer
            </button>
            <button type="button" class="btn btn-success"
                    :disabled="!csvPreview.toCreate.length || savingImport"
                    @click="saveImportedCategories">
              <i class="fe fe-check me-1"></i>
              Enregistrer ({{ csvPreview.toCreate.length }})
            </button>
          </div>
        </div>
      </div>
    </div>

  </div>
</template>

<script>
  import ModalCreateFamily from "../../../components/modals/ModalCreateFamily.vue";
  import TableWithFilters from "../../../components/TableWithFilters.vue";
  import {
    familyColumns,
    familyFilterableFields,
    familyRowActions,
  } from "../../../helpers/constants";
  import * as XLSX from "xlsx";
  import {confirmAction, showValidationErrors} from "../../../helpers/helpers";
  import {API} from "../../../helpers/api-endpoints";

  export default {
    name: "Families",
    components: {
      TableWithFilters,
      ModalCreateFamily,
    },
    data() {
      return {
        families: [],
        selectedFamily: null,
        columns: familyColumns,
        filterableFields: familyFilterableFields,
        rowActions: familyRowActions,
        csvPreview: {
          total: 0,
          toCreate: [],
          existingDb: [],
          duplicatesFile: [],
          invalid: [],
          headers: [],
        },
        savingImport: false,
      };
    },
    methods: {
      async saveImportedCategories() {
        if (!this.csvPreview.toCreate.length) return;

        const { confirmed } = await confirmAction("validate");
        if (!confirmed) return;

        this.savingImport = true;
        this.$loading(true);

        try {
          // Version séquentielle (simple). Si tu as un endpoint batch, remplace par un POST /import.
          let ok = 0, ko = 0;
          for (const c of this.csvPreview.toCreate) {
            try {
              await this.$http.post(API.REGISTRY_FAMILIES, { code: c.code, label: c.label, description: c.description });
              ok++;
            } catch (e) {
              console.error("Échec création :", c, e);
              ko++;
            }
          }

          this.$toast.success(`Import terminé. Créées: ${ok}, Échouées: ${ko}`);
          $('#modal-import-preview').modal('hide');
          this.resetCsvPreview();
          await this.loadFamilies();
        } catch (err) {
          console.error(err);
          this.$toast.error("Erreur lors de l'enregistrement des catégories.");
        } finally {
          this.$loading(false);
          this.savingImport = false;
        }
      },
      // <<< Reset preview
      resetCsvPreview() {
        this.csvPreview = {
          total: 0, toCreate: [], existingDb: [], duplicatesFile: [], invalid: [], headers: [],
        };
      },
      buildCsvPreview(text) {
        const data = new Uint8Array(text);
        const workbook = XLSX.read(data, { type: "array" });

        // On cherche la feuille nommée "Famille"
        const targetSheetName = workbook.SheetNames.find(name => name.toLowerCase() === "famille".toLowerCase());

        if (!targetSheetName) {
          console.error("❌ La feuille 'Famille' est introuvable dans le fichier Excel.");
          this.$toast.error("❌ La feuille 'Famille' est introuvable dans le fichier Excel.");
          this.resetCsvPreview();
          return;
        }

        const worksheet = workbook.Sheets[targetSheetName];

        // Convertir en JSON
        const jsonData = XLSX.utils.sheet_to_json(worksheet, { header: 1 });

        if (!jsonData.length) {
          this.$toast.error("Fichier XLSX vide.");
          this.resetCsvPreview();
          return;
        }

        const headersRaw = jsonData[0] || [];
        // rendre la recherche des index insensible à la casse et tolérante aux espaces
        const headers = headersRaw.map(h => (h ?? "").toString().trim());
        const findIndexIgnoreCase = (arr, key) => arr.findIndex(h => h.toString().toLowerCase() === key.toLowerCase());

        // Vérification des colonnes
        const codeIdx = findIndexIgnoreCase(headers, "code");
        const labelIdx = findIndexIgnoreCase(headers, "label");
        const descriptionIdx = findIndexIgnoreCase(headers, "description");

        if (codeIdx === -1 || labelIdx === -1) {
          this.$toast.error("Le XLSX doit contenir les colonnes 'code' et 'label'.");
          this.resetCsvPreview();
          return;
        }

        const rows = [];
        for (let i = 1; i < jsonData.length; i++) {
          const line = jsonData[i];
          const row = {
            code: (line[codeIdx] ?? "").toString().trim(),
            label: (line[labelIdx] ?? "").toString().trim(),
            description: (descriptionIdx !== -1 ? (line[descriptionIdx] ?? "").toString().trim() : ""),
          };
          rows.push(row);
        }

// Nettoyage / stats
        const cleaned = rows.filter(r => r.code || r.label);
        const invalid = cleaned.filter(r => !r.code || !r.label);

// Doublons fichier (par code insensible à la casse)
        const seen = new Set();
        const duplicatesFile = [];
        const uniqueByFile = [];

        for (const r of cleaned) {
          const key = (r.code || "").toLowerCase();
          if (!r.code || !r.label) continue; // laisser aux invalides
          if (seen.has(key))
            duplicatesFile.push(r);
          else {
            seen.add(key); uniqueByFile.push(r);
          }
        }

/// Déjà en base
        const existingCodes = new Set(this.families.map(c => (c.code || "").toLowerCase()));

        const existingDb = uniqueByFile.filter(r => existingCodes.has((r.code || "").toLowerCase()));
        const toCreate = uniqueByFile.filter(r => !existingCodes.has((r.code || "").toLowerCase()));

        this.csvPreview = {
          total: rows.length,
          toCreate,
          existingDb,
          duplicatesFile,
          invalid,
          headers,
        };

        $('#modal-import-preview').modal('show'); // <<< ouvre la modale

      },
      async importCSV(event) {
        const file = event.target.files[0];
        if (!file) return;

        const reader = new FileReader();
        reader.onload = async (e) => {
          try {
            const text = e.target.result || "";
            this.buildCsvPreview(e.target.result);  // <<< construit les listes
          } catch (err) {
            console.error("Erreur import CSV :", err);
            this.$toast.error("Erreur lors de la lecture du CSV.");
          } finally {
            event.target.value = ""; // reset input
          }
        };
        reader.readAsArrayBuffer(file, "UTF-8");
      },
      triggerFileInput() { this.$refs.fileInput.click(); },
      showCreateFamilyModal() {
        this.selectedFamily = null;
        $('#modal-create-family').modal('show');
      },
      loadFamilies() {
        this.$loading(true);
        this.$http
            .get(API.REGISTRY_FAMILIES)
            .then(({ data }) => {
              this.families = data;
            })
            .catch((err) => {
              console.error("Erreur lors du chargement des familles :", err);
            })
            .finally(() => this.$loading(false));
      },
      onEdit(item) {
        this.selectedFamily = { ...item };
        $('#modal-create-family').modal('show');
      },
      async onDelete(item) {
        try {
          const { confirmed } = await confirmAction("delete");
          if (!confirmed) return;

          this.$loading(true);
          await this.$http.delete(API.REGISTRY_FAMILY_DELETE(item.id)); //
          this.$toast.success("Famille supprimée avec succès !");
          await this.loadFamilies();
        } catch (err) {
          console.error("Erreur suppression Famille :", err);
          showValidationErrors(err);
          this.$toast.error("Impossible de supprimer la Famille.");
        } finally {
          this.$loading(false);
        }
      },
      exportCSV() {
        const headers = this.columns.map(col => col.label);
        const rows = this.families.map(row =>
            this.columns.map(col => `"${row[col.key] ?? ''}"`).join(",")
        );
        const csvContent = [headers.join(","), ...rows].join("\n");

        const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
        const url = URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.setAttribute("href", url);
        link.setAttribute("download", "familles.csv");
        link.click();
        URL.revokeObjectURL(url);
      },
    },
    mounted() {
      this.loadFamilies();
    },
  };
</script>
